<template>
  <div class='user-details'>
    <dean-two-column-layout :title="headerTitle">
      <dean-section-title-item
          :icon="saveIcon"
          slot="title-items"
          :content="true"
          :large="true"
          @click.native="save"
      >
        Opslaan
      </dean-section-title-item>
      <div class="column" slot="left">
        <dean-form-row id="fullName" label="Volledige naam" v-model="data.fullName" type="text" :required-field="true"
                       :warning="$v.data.fullName.$error" @focus="$v.data.fullName.$touch()"/>
        <dean-form-row id="emailAddress" label="E-mailadres" v-model="data.emailAddress" type="text"
                       :required-field="true"
                       :warning="$v.data.emailAddress.$error" @focus="$v.data.emailAddress.$touch()"/>
        <dean-multi-select-form-row id="roles" key="roles" label="Rollen" placeholder="kies rollen"
                                    :multiple="true" :options="availableRolesList" :value="selectedRolesList"
                                    @select="addRole" @remove="removeRole"/>
      </div>
    </dean-two-column-layout>
  </div>
</template>

<script lang="ts">
  import {Vue, Component} from 'vue-property-decorator';
  import axios from 'axios';
  import {
    deanSectionTitle,
    deanSectionTitleItem,
    saveIcon,
  } from '@deanone/dean-components';
  import DeanFormRow from '@/components/deanComponents/deanFormRow.vue';
  import DeanTwoColumnLayout from '@/components/deanComponents/deanTwoColumnLayout.vue';
  import DeanMultiSelectFormRow from '@/components/deanComponents/deanMultiSelectFormRow.vue';
  import {validationMixin} from 'vuelidate';
  import {required} from 'vuelidate/lib/validators';
  import {Validations} from 'vuelidate-property-decorators';
  import {createNotification} from '@/mixins/validations';
  import {mixins} from 'vue-class-component';
  import sideMenuAdminMixin from '@/mixins/sideMenuAdminMixin';

  @Component({
    components: {
      deanSectionTitle,
      deanSectionTitleItem,
      DeanFormRow,
      DeanTwoColumnLayout,
      DeanMultiSelectFormRow,
    },
    mixins: [validationMixin],
  })
  export default class Details extends mixins(sideMenuAdminMixin) {
    // DATA
    public userName: string = '';
    public data: {
      fullName: string,
      emailAddress: string,
    } = {
      fullName: '',
      emailAddress: '',
    };
    public saveIcon: any = saveIcon;

    public roles: Array<{value: string, label: string}> = [];
    public availableRoles: Array<{value: string, label: string}> = [];
    public selectedRoles: Array<{value: string, label: string}> = [];

    // COMPUTED
    @Validations()
    get validations() {
      return {
        data: {
          fullName: {required},
          emailAddress: {required},
        },
      };
    }

    get headerTitle(): string {
      return 'Gebruiker ' + this.userName + ' wijzigen';
    }

    get availableRolesList() {
      return this.availableRoles;
    }

    get selectedRolesList() {
      this.updateAvailableRoles();
      return this.selectedRoles;
    }

    // METHODS
    public addRole(field: any) {
      this.selectedRoles.push(field);
      this.updateAvailableRoles();
    }

    public removeRole(field: any) {
      this.selectedRoles = this.selectedRoles.filter((role: any) => role.value !== field.value);
      this.updateAvailableRoles();
    }

    public updateAvailableRoles() {
      if (this.selectedRoles.length <= 0) {
        this.availableRoles = this.roles;
        return;
      }

      const selectedRoleIds: string[] = this.selectedRoles.map((role: any) => role.value);
      this.availableRoles = this.roles.filter((role: any) => !selectedRoleIds.includes(role.value));
    }
    public save() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.$store.dispatch('setLoadOverlay', true);

        const requestData = {
          id: this.$route.params.id,
          fullName: this.data.fullName,
          emailAddress: this.data.emailAddress,
          roleIds: this.selectedRoles.map((role: any) => role.value),
        };

        axios.post('/-/trade/api/admin.user.update', requestData)
        .then(
          (response: any) => {
            this.$toast(this.$store.state.main.notification.messages.dataSaved);
          },
          (error: any) => {
            this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
          },
        ).finally(() => {
          this.$store.dispatch('setLoadOverlay', false);
        });
      } else {
        this.$toast(createNotification(this.$v));
      }
    }

    public created() {
      this.$store.dispatch('setLoadOverlay', true);

      // Fetch all available roles
      axios.post('/-/trade/api/admin.roles.list', {})
      .then(
        (response) => {
          this.roles = response.data.data.map((role: any) => ({
            value: role.id,
            label: role.name,
          }));
        },
        (error) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      );

      // Fetch user info including assigned roles
      axios.post('/-/trade/api/admin.user.info', {
          id: this.$route.params.id,
      }).then(
        (response) => {
          this.data = {
            fullName: response.data.fullName,
            emailAddress: response.data.email,
          };
          this.userName = response.data.fullName;

          // Set selected roles from user data
          if (response.data.roles) {
            this.selectedRoles = response.data.roles.map((role: any) => ({
              value: role.id,
              label: role.name,
            }));
          }
        },
        (error) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      ).finally(() => {
        this.$store.dispatch('setLoadOverlay', false);
        this.buildSideMenuAdmin('Gebruiker (' + this.userName + ')');
      });
    }
  }
</script>

<style lang="scss" scoped>
</style>